import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import {
  PaperButtonComponent,
  PaperIconComponent,
} from '@ricoh-paper/angular';
import { PaperRichTextComponent } from '@ricoh-paper/angular/rich-text';

/**
 * NotesSectionComponent — viewer-private planning notes for a meeting.
 *
 * Two surfaces in one section:
 *   1. **Notes textarea** — bound to `service.internalNotes`. Autosaves
 *      to the backend ~1 s after the user stops typing (existing
 *      meetings only). For new meetings the notes are held locally and
 *      flushed by `submitBooking` after the meeting is created.
 *      Editable ONLY while `service.internalNotesState()` is 'ready';
 *      see that signal's doc for why. In short: this editor is shared
 *      across meetings (the panel is never destroyed), so an autosave
 *      fired over a buffer we could not confirm belongs to the meeting
 *      on screen is a write of one person's private notes onto another
 *      meeting.
 *   2. **AI brief** — Haiku-backed planning brief. Reads subject +
 *      attendees + notes and produces an agenda outline + prep
 *      questions + a suggestion. Surfaced as a one-tap button; silent
 *      degradation when GenAI isn't configured.
 *
 * Privacy contract (loud + clear in the copy):
 *   - Notes NEVER appear on the calendar invite.
 *   - Other attendees can't see them.
 *   - The AI brief generation sends the notes to Vertex AI for the
 *     duration of the call only — no other downstream consumer.
 */
@Component({
  selector: 'im-notes-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    PaperButtonComponent,
    PaperIconComponent,
    PaperRichTextComponent,
    TranslocoPipe,
  ],
  template: `
    <div class="ns">
      <!-- Public agenda — the calendar invite body. Everyone on the
           meeting sees this. Same field that used to live in More
           Options; promoted out so the public/private split is
           obvious in one tab. View-mode suppresses this surface
           because the panel renders the agenda as a read-only card
           below the find-time strip; duplicating an editable agenda
           here would let the viewer "edit" a meeting they may not
           own. The PRIVATE NOTES surface below is always shown — it's
           the viewer's own write space, valid in both create AND view. -->
      @if (!viewMode()) {
        <section class="ns__field">
          <header class="ns__field-head">
            <span class="ns__field-label">{{ 'viewer.book_meeting_panel.notes.agenda' | transloco }}</span>
            <span class="ns__field-visibility ns__field-visibility--public">
              <paper-icon name="users" [size]="11" aria-hidden="true" />
              {{ 'viewer.book_meeting_panel.notes.visible_to_all_attendees' | transloco }}
            </span>
          </header>
          <p class="ns__field-hint">
            {{ 'viewer.book_meeting_panel.notes.agenda_hint' | transloco }}
          </p>
          <paper-rich-text
            class="ns__editor ph-mask"
            [placeholder]="'viewer.book_meeting_panel.notes.agenda_placeholder' | transloco"
            [value]="service.description()"
            (contentChange)="onAgendaChanged($event)" />
        </section>
      }

      <!-- Private notes — viewer-scoped, never shared. View-mode
           drops the field-label (the outer .bmp__section-title
           already reads "Your notes" + shield icon) and keeps only
           the "Private to you" badge inline so the privacy framing
           stays loud without duplicating the heading. Create-mode
           still renders the full head because it sits below the
           public Agenda editor and the label disambiguates them. -->
      <section class="ns__field">
        @if (!viewMode()) {
          <header class="ns__field-head">
            <span class="ns__field-label">{{ 'viewer.book_meeting_panel.notes.your_notes' | transloco }}</span>
            <span class="ns__field-visibility ns__field-visibility--private">
              <paper-icon name="shield" [size]="11" aria-hidden="true" />
              {{ 'viewer.book_meeting_panel.notes.private_to_you' | transloco }}
            </span>
          </header>
        } @else {
          <header class="ns__field-head ns__field-head--bare">
            <span class="ns__field-visibility ns__field-visibility--private">
              <paper-icon name="shield" [size]="11" aria-hidden="true" />
              {{ 'viewer.book_meeting_panel.notes.private_to_you' | transloco }}
            </span>
          </header>
        }
        <p class="ns__field-hint">
          {{ 'viewer.book_meeting_panel.notes.private_hint' | transloco }}
        </p>
        <!-- DISABLED unless the read succeeded. A typeable box holding
             notes we could not confirm belong to THIS meeting is how the
             1 s autosave used to move private notes between meetings —
             see BookMeetingService.internalNotesState. -->
        <paper-rich-text
          class="ns__editor ph-mask"
          data-testid="ns-private-notes"
          [placeholder]="privatePlaceholder()"
          [disabled]="service.internalNotesState() !== 'ready'"
          [value]="service.internalNotes()"
          (contentChange)="onNotesChanged($event)" />
        @if (service.internalNotesState() === 'failed') {
          <div class="ns__notes-failed" role="alert" data-testid="ns-notes-failed">
            <paper-icon name="alert-triangle" [size]="13" aria-hidden="true" />
            <span>{{ 'viewer.book_meeting_panel.notes.load_failed' | transloco }}</span>
            <paper-button
              intent="ghost"
              class="ns__notes-retry"
              data-testid="ns-notes-retry"
              (click)="retryNotes()">
              {{ 'viewer.book_meeting_panel.notes.try_again' | transloco }}
            </paper-button>
          </div>
        }
        <div class="ns__field-foot">
          @if (savedLabel(); as label) {
            <span class="ns__saved" aria-live="polite">
              <paper-icon name="check" [size]="10" aria-hidden="true" />
              {{ label }}
            </span>
          } @else if (service.internalNotesSaving()) {
            <span class="ns__saved ns__saved--loading" aria-live="polite">
              {{ 'viewer.book_meeting_panel.notes.saving' | transloco }}
            </span>
          } @else {
            <span class="ns__saved-placeholder">{{ 'viewer.book_meeting_panel.notes.autosaves' | transloco }}</span>
          }
        </div>
      </section>

      <!-- The inline "Help me plan this" / "Draft a brief" surface that
           used to live here has moved into the AI side rail's
           Plan-this-with-AI card (right-hand column of the panel) so
           the affordance isn't duplicated. service.loadMeetingBrief()
           is still called from there. -->
    </div>
  `,
  styles: [`
    :host { display: block; }

    .ns {
      display: flex;
      flex-direction: column;
      /* 28px between the two big editors so the agenda and the private
         notes feel like distinct surfaces, not a single split textarea. */
      gap: 28px;
    }

    /* Field — wraps an editor + its labelled head + foot. */
    .ns__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .ns__field-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    /* View-mode variant — outer section-title carries the heading,
       this head only renders the privacy badge so the two surfaces
       don't repeat "Your notes". Right-aligned to match the badge's
       position in the full head. */
    .ns__field-head--bare {
      justify-content: flex-end;
    }

    .ns__field-label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: var(--text-secondary);
    }

    /* Visibility chip — the load-bearing affordance for the public/
       private contract. Green for public (shared with attendees),
       brand-indigo for private (viewer-only). Both use the same shape
       so the eye recognises them as a pair. */
    .ns__field-visibility {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px;
      border-radius: var(--radius-pill, 999px);
      font-size: 10.5px;
      font-weight: 600;
      letter-spacing: 0.02em;
    }

    .ns__field-visibility--public {
      background: color-mix(in srgb, var(--success-strong) 14%, transparent);
      color: var(--success-strong);
    }

    .ns__field-visibility--private {
      background: color-mix(in srgb, var(--brand-primary) 14%, transparent);
      color: var(--brand-primary);
    }

    .ns__field-hint {
      margin: 0;
      font-size: 12px;
      line-height: 1.5;
      color: var(--text-secondary);
    }

    /* Editor — give paper-rich-text room to breathe. Min-height ensures
       even an empty editor feels substantial rather than a one-line
       textbox. */
    .ns__editor {
      display: block;
      min-height: 160px;
    }

    .ns__field-foot {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
      font-size: 11px;
      color: var(--text-tertiary);
    }

    .ns__saved {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--success-strong);
    }

    .ns__saved--loading { color: var(--text-tertiary); }

    .ns__saved-placeholder {
      color: var(--text-tertiary);
      font-style: italic;
    }

    /* Failed-read strip. Loud on purpose: a silently disabled editor
       reads as a broken app, and the reason it is disabled is the whole
       point — anything typed would land on a note the viewer can't see. */
    .ns__notes-failed {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      padding: 9px 11px;
      border-radius: var(--radius-md);
      border: 1px solid color-mix(in srgb, var(--error-strong) 28%, transparent);
      background: color-mix(in srgb, var(--error-strong) 9%, transparent);
      color: var(--error-strong);
      font-size: 12px;
      line-height: 1.45;
    }

    .ns__notes-failed paper-icon { flex: none; }
    .ns__notes-failed span { flex: 1 1 200px; min-width: 0; }
    .ns__notes-retry { flex: none; font-size: 12px; font-weight: 600; }
  `],
})
export class NotesSectionComponent {
  protected readonly service = inject(BookMeetingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** When true, suppress the public-agenda editor (the panel renders
   *  agenda as a read-only card elsewhere in view-mode) but keep the
   *  private-notes editor so the viewer can still attach their own
   *  prep notes to a meeting they don't organise. Notes round-trip
   *  via the existing `saveMeetingNotes` mutation (no extra wiring). */
  readonly viewMode = input<boolean>(false);

  /** Local debounce handle for the autosave. Cleared on destroy. */
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Snapshot of the last value we persisted — guards against firing
   *  a no-op save when the textarea fires ngModelChange with the same
   *  value (e.g. on focus). */
  private lastSavedValue: string | null = null;

  /** Ticks every minute so the "Saved 2 min ago" copy stays live
   *  without re-rendering the whole component. */
  private readonly minuteTick = signal(0);

  /** Placeholder tracks the read state. A disabled editor still offering
   *  "Prep, talking points…" reads as broken rather than as waiting. */
  protected readonly privatePlaceholder = computed(() => {
    const t = (k: string) => this.transloco.translate(k);
    switch (this.service.internalNotesState()) {
      case 'loading':
        return t('viewer.book_meeting_panel.notes.private_loading');
      case 'failed':
        return t('viewer.book_meeting_panel.notes.private_unavailable');
      default:
        return t('viewer.book_meeting_panel.notes.private_placeholder');
    }
  });

  /** Friendly "Saved 2 min ago" / "Saved just now" label. Null when
   *  no save has happened yet OR a save is in flight. */
  protected readonly savedLabel = computed<string | null>(() => {
    this.minuteTick();
    this.locale.locale();
    if (this.service.internalNotesSaving()) return null;
    const at = this.service.internalNotesSavedAt();
    if (!at) return null;
    const elapsedMs = Date.now() - at.getTime();
    const t = (k: string, params?: Record<string, unknown>) => this.transloco.translate(k, params);
    if (elapsedMs < 30_000) return t('viewer.book_meeting_panel.notes.saved_just_now');
    const mins = Math.floor(elapsedMs / 60_000);
    if (mins === 0) return t('viewer.book_meeting_panel.notes.saved_a_moment_ago');
    if (mins === 1) return t('viewer.book_meeting_panel.notes.saved_1_min_ago');
    if (mins < 60) return t('viewer.book_meeting_panel.notes.saved_n_min_ago', { count: mins });
    const hrs = Math.floor(mins / 60);
    return t('viewer.book_meeting_panel.notes.saved_n_hr_ago', { count: hrs });
  });

  constructor() {
    // 60s tick for the "Saved N min ago" copy. Cheap; cleaned up on
    // destroy. Outside the zone would be nicer but the signal update
    // needs zone re-entry anyway.
    const interval = setInterval(() => this.minuteTick.update(t => t + 1), 60_000);
    this.destroyRef.onDestroy(() => {
      clearInterval(interval);
      if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    });

    // Reset the "last saved" snapshot whenever the loaded meeting
    // changes — otherwise switching from meeting A to meeting B could
    // suppress the first save on B because the textarea momentarily
    // shows A's value.
    effect(() => {
      this.service.loadedMeetingId();
      this.lastSavedValue = null;
    });
  }

  /**
   * Rich-text change handler for private notes. Writes to the signal
   * immediately for reactive UI, then schedules a debounced save.
   * 1 s feels snappy without spamming the backend during a paragraph-
   * long paste. paper-rich-text emits the editor's HTML on every edit.
   */
  protected onNotesChanged(html: string): void {
    // Belt and braces with the template's [disabled]. Text we could not
    // READ must never become text we WRITE, so outside 'ready' nothing is
    // buffered into the shared signal and no autosave is armed. The
    // attribute alone is not enough: an output emitted programmatically
    // (or, for a plain control, a dispatched `input` event) reaches this
    // handler regardless of what the DOM thinks about disabled.
    if (this.service.internalNotesState() !== 'ready') return;
    this.service.internalNotes.set(html ?? '');
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      const current = this.service.internalNotes();
      if (current === this.lastSavedValue) return; // No-op
      // saveInternalNotes() is a no-op when there's no loadedMeetingId
      // (new bookings) — the notes are kept locally and flushed after
      // the meeting is created via submitBooking → flushNotesAfterCreate.
      void this.service.saveInternalNotes();
      this.lastSavedValue = current;
    }, 1000);
  }

  /**
   * Rich-text change handler for the public agenda. Routes through
   * `service.description` — the existing field that's already wired
   * into the submission path (Microsoft Graph accepts HTML for the
   * calendar body, so the rich content survives end-to-end). No
   * autosave for new meetings; the value is included in the
   * createBooking payload and persisted server-side on submit.
   */
  protected onAgendaChanged(html: string): void {
    this.service.description.set(html ?? '');
  }

  /**
   * Re-read the notes after a failed load — the only way out of 'failed',
   * and therefore the only way this editor becomes writable again without
   * reopening the panel.
   */
  protected retryNotes(): void {
    void this.service.retryInternalNotes();
  }
}
